// Bench diagnostics for the realtime stack.
//
// The hard part of debugging "everything froze while I was on another screen" is
// that by the time you look, the console has scrolled away and the interesting
// state lives inside phoenix.js and @absinthe/socket. This module keeps a rolling
// history of socket events and exposes one command that prints the full picture
// plus a read on what it means.
//
// From DevTools on the bench:
//
//   __socket.report()     snapshot + diagnosis of the current state
//   __socket.history()    last events with timestamps (survives a scrolled console)
//   __socket.reconnect()  force a correct reconnect, to confirm recovery works
//   __socket.verbose()    turn on phoenix's own join/push/receive log, then reload

const HISTORY_LIMIT = 80;
const VERBOSE_KEY = "socket_verbose";
const STALE_SECONDS = 45;

const history = [];

export const recordEvent = (event, detail) => {
  history.push({
    at: new Date().toISOString(),
    event,
    detail: detail === undefined ? null : String(detail),
  });

  if (history.length > HISTORY_LIMIT) history.shift();
};

// Opt-in because phoenix's logger prints every heartbeat, push and reply — great
// while hunting a rejoin problem, unusable as a default.
export const isVerbose = () => {
  try {
    return window.localStorage.getItem(VERBOSE_KEY) === "1";
  } catch (error) {
    return false;
  }
};

export const phoenixLogger = (kind, message, data) => {
  // eslint-disable-next-line no-console
  console.info("[phoenix]", kind, message, data);
};

const diagnose = ({
  connection,
  channelState,
  activeNotifiers,
  secondsSinceLastMessage,
}) => {
  if (connection !== "open") {
    return "socket não está aberto; deve estar em backoff — confira history() para ver se está em loop";
  }

  if (channelState !== "joined") {
    return `canal absinthe está "${channelState}" — nenhuma subscription recebe nada até ele entrar em joined`;
  }

  if (activeNotifiers === 0) {
    return "nenhuma subscription ativa — normal fora do painel de instrumentos";
  }

  if (secondsSinceLastMessage > STALE_SECONDS) {
    return "aberto, joined e com subscriptions, mas sem tráfego: é a assinatura do canal morto no servidor "
      + "(cliente acha que está joined, servidor nunca recebeu o phx_join). Chame reconnect().";
  }

  return "aparentemente saudável";
};

export const installDebugHandle = ({ socket, absintheSocket, monitor }) => {
  const snapshot = () => {
    const { channel } = absintheSocket;
    const notifiers = absintheSocket.notifiers || [];
    const stats = monitor.stats();

    return {
      connection: socket.connectionState(),
      secondsSinceLastMessage: Math.round((Date.now() - stats.lastActivityAt) / 1000),
      consecutiveFailures: stats.consecutiveFailures,
      channelState: channel && channel.state,
      // Never reset by the library: if this is true while the channel is not
      // joined, absinthe will never re-join on its own.
      channelJoinCreated: absintheSocket.channelJoinCreated,
      notifiers: notifiers.length,
      activeNotifiers: notifiers.filter(notifier => notifier.isActive).length,
      tabHidden: document.hidden,
      verboseLogging: isVerbose(),
    };
  };

  // eslint-disable-next-line no-underscore-dangle
  window.__socket = {
    report: () => {
      const state = snapshot();
      const diagnosis = diagnose(state);

      // eslint-disable-next-line no-console
      console.info("[socket] report", state, "\n→", diagnosis);

      return { ...state, diagnosis };
    },

    history: () => history.slice(),

    reconnect: () => monitor.reconnect("manual (__socket.reconnect)"),

    verbose: (enabled = true) => {
      try {
        window.localStorage.setItem(VERBOSE_KEY, enabled ? "1" : "0");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[socket] localStorage indisponível, não foi possível salvar a preferência");
        return;
      }

      // eslint-disable-next-line no-console
      console.info(`[socket] log verboso ${enabled ? "ligado" : "desligado"} — recarregue a página`);
    },
  };
};
